import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useDeleteBudgetYear,
  useFinanceSettingsStore,
  useUpdateBudgetYear
} from "features/finances";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const budgetYearSchema = yup.object({
  title: yup.string().required("Title is required"),
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().required("End date is required"),
  description: yup.string().optional()
});

export function UpdateBudgetYearModal({ data }) {
  const updateBudgetYearOpen = useFinanceSettingsStore(
    state => state.updateBudgetYearOpen
  );
  const closeUpdateBudgetYearModal = useFinanceSettingsStore(
    state => state.closeUpdateBudgetYearModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const updateBudgetYearMutation = useUpdateBudgetYear();
  const deleteBudgetYearMutation = useDeleteBudgetYear();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: data?.title || "",
      startDate: data?.start_date || "",
      endDate: data?.end_date || "",
      description: data?.description || ""
    },
    resolver: yupResolver(budgetYearSchema)
  });

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  useEffect(() => {
    if (data) {
      setValue("title", data.title || "");
      setValue("startDate", data.start_date || "");
      setValue("endDate", data.end_date || "");
      setValue("description", data.description || "");
    }
  }, [data]);

  const onSubmit = values => {
    const budgetYearData = {
      title: values.title,
      start_date: values.startDate,
      end_date: values.endDate,
      description: values.description
    };

    updateBudgetYearMutation.mutate(
      {
        churchId: selectedChurch?.id,
        budgetYearId: data?.id,
        budgetYearData
      },
      {
        onSuccess: () => {
          reset();
        }
      }
    );
  };

  return (
    <BaseModal
      title="Update Budget Year"
      isOpen={updateBudgetYearOpen}
      onClose={closeUpdateBudgetYearModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button
            flavor="neutral"
            size="small"
            onClick={() => {
              deleteBudgetYearMutation.mutate({
                churchId: selectedChurch?.id,
                budgetYearId: data?.id
              });
            }}
            disabled={
              deleteBudgetYearMutation.isLoading ||
              updateBudgetYearMutation.isLoading
            }
          >
            Delete
          </Button>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              flavor="white"
              size="small"
              onClick={closeUpdateBudgetYearModal}
              disabled={
                updateBudgetYearMutation.isLoading ||
                deleteBudgetYearMutation.isLoading
              }
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSubmit(onSubmit)}
              disabled={
                updateBudgetYearMutation.isLoading ||
                deleteBudgetYearMutation.isLoading
              }
            >
              Save changes
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="name">Title</Label>
        <Input {...register("title")} />
        <ErrorMessage
          errors={errors}
          name="title"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="startDate">Start Date</Label>
        <EthiopianDateField
          size="small"
          value={startDate}
          onChange={e => setValue("startDate", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="startDate"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="endDate">End Date</Label>
        <EthiopianDateField
          size="small"
          value={endDate}
          onChange={e => setValue("endDate", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="endDate"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="description">Description (optional)</Label>
        <Input {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError error={updateBudgetYearMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
